import { useState, useEffect, useRef } from 'react';
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaMapMarkerAlt, FaInfoCircle, FaCalendarCheck, FaImage, FaUpload } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

const EventManagement = () => {
  // Estado para armazenar a lista de eventos
  const [events, setEvents] = useState([]);
  // Estado para o carregamento de dados
  const [loading, setLoading] = useState(true);
  // Estado para mensagens de erro
  const [error, setError] = useState(null);
  // Estado para indicar o carregamento do upload de imagem
  const [uploadLoading, setUploadLoading] = useState(false);

  // Estado para o formulário de adicionar/editar evento
  const [formData, setFormData] = useState({
    id: null,
    nome: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    local: '',
    status: 'pendente',
    ativo: true,
    imagem: null,
    imagem_url: ''
  });
  
  // Estado para controlar se estamos editando ou adicionando
  const [isEditing, setIsEditing] = useState(false);
  // Estado para controlar o modal
  const [showModal, setShowModal] = useState(false);
  // Estado para controlar a pré-visualização da imagem
  const [imagePreview, setImagePreview] = useState(null);
  // Obter contexto de autenticação
  const { hasPermission, user } = useAuth();
  // Referência para o input de arquivo
  const fileInputRef = useRef(null);

  // Estilos para o modal flutuante
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    body: {
      padding: '2rem',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.5rem 2rem',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cancelButton: {
      border: '1px solid #dee2e6',
      background: 'white',
      color: '#495057',
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: '#212529',
      color: 'white',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
    },
    imageUploadContainer: {
      border: '2px dashed #dee2e6',
      borderRadius: '10px',
      padding: '1.5rem',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginBottom: '1.5rem',
      position: 'relative',
      overflow: 'hidden'
    },
    imagePreview: {
      width: '100%',
      height: '200px',
      objectFit: 'cover',
      borderRadius: '8px',
      marginBottom: '1rem'
    },
    uploadIcon: {
      fontSize: '2rem',
      color: '#6c757d',
      marginBottom: '0.5rem'
    },
    uploadText: {
      color: '#6c757d',
      marginBottom: '0.5rem'
    },
    uploadButton: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      color: '#495057',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    removeImageButton: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      border: 'none',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#dc3545',
      zIndex: 10
    }
  };

  // Carregar eventos do Supabase
  const fetchEvents = async () => {
    try {
      setLoading(true);
      console.log('Iniciando carregamento de eventos...');
      
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('*')
        .order('id', { ascending: false });
      
      if (error) {
        console.error('Erro ao buscar eventos:', error);
        throw error;
      }
      
      console.log('Eventos carregados:', data);
      setEvents(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      setError('Não foi possível carregar a lista de eventos.');
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    fetchEvents();
  }, []);

  // Adicionar estilo de animação ao carregar o componente
  useEffect(() => {
    // Criar elemento de estilo para a animação
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .form-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        font-size: 0.9rem;
        color: #495057;
        transition: all 0.3s ease;
      }
      
      .form-input:focus {
        outline: none;
        border-color: #212529;
        box-shadow: 0 0 0 3px rgba(33, 37, 41, 0.1);
      }
      
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
      }
      
      .form-group {
        margin-bottom: 1.5rem;
      }
      
      .modal-close-btn:hover {
        background-color: #f8f9fa;
        color: #212529;
        transform: rotate(90deg);
      }
      
      .btn-cancel:hover {
        background-color: #f8f9fa;
      }
      
      .btn-submit:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }
      
      .row {
        display: flex;
        flex-wrap: wrap;
        margin-right: -15px;
        margin-left: -15px;
      }
      
      .col-md-6 {
        position: relative;
        width: 100%;
        padding-right: 15px;
        padding-left: 15px;
      }
      
      @media (min-width: 768px) {
        .col-md-6 {
          flex: 0 0 50%;
          max-width: 50%;
        }
      }
      
      .text-muted {
        color: #6c757d;
        font-size: 0.8rem;
        margin-top: 0.25rem;
      }

      .image-upload-container:hover {
        border-color: #adb5bd;
        background-color: #f8f9fa;
      }

      .upload-button:hover {
        background-color: #e9ecef;
      }

      .spinner {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    // Limpar ao desmontar
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Impedir o scroll quando o modal estiver aberto
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  // Manipular mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Manipular seleção de imagem
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Verificar o tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione um arquivo de imagem válido.');
      return;
    }

    // Verificar o tamanho do arquivo (limitar a 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    setFormData({
      ...formData,
      imagem: file
    });

    // Criar URL para pré-visualização
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Função para remover a imagem
  const handleRemoveImage = () => {
    setFormData({
      ...formData,
      imagem: null,
      imagem_url: isEditing ? formData.imagem_url : '' // Manter a URL existente se estiver editando
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger o click no input de arquivo
  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  // Abrir o modal para adicionar um novo evento
  const handleAddNew = () => {
    setFormData({
      id: null,
      nome: '',
      descricao: '',
      data_inicio: '',
      data_fim: '',
      local: '',
      status: 'pendente',
      ativo: true,
      imagem: null,
      imagem_url: ''
    });
    setImagePreview(null);
    setIsEditing(false);
    setShowModal(true);
  };

  // Abrir o modal para editar um evento existente
  const handleEdit = (event) => {
    // Formatar datas para o input date
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    setFormData({
      ...event,
      data_inicio: formatDate(event.data_inicio),
      data_fim: formatDate(event.data_fim),
      imagem: null // Iniciar sem arquivo, apenas URL
    });

    // Definir preview da imagem existente
    setImagePreview(event.imagem_url || null);
    setIsEditing(true);
    setShowModal(true);
  };

  // Excluir um evento
  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este evento?')) {
      try {
        // Primeiro, obter o evento para pegar a URL da imagem
        const { data: eventData, error: fetchError } = await supabase
          .from('caex_eventos')
          .select('imagem_url')
          .eq('id', id)
          .single();
        
        if (fetchError) throw fetchError;
        
        // Excluir o evento do banco de dados
        const { error } = await supabase
          .from('caex_eventos')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        
        // Se houver uma imagem, excluí-la do Storage
        if (eventData.imagem_url) {
          const imagePath = eventData.imagem_url.split('/').pop();
          const { error: storageError } = await supabase
            .storage
            .from('eventos')
            .remove([imagePath]);
          
          if (storageError) {
            console.error('Erro ao excluir imagem do Storage:', storageError);
            // Não interromper o fluxo se a exclusão da imagem falhar
          }
        }
        
        // Atualizar a lista de eventos
        setEvents(events.filter(event => event.id !== id));
      } catch (error) {
        console.error('Erro ao excluir evento:', error);
        alert('Não foi possível excluir o evento. Por favor, tente novamente.');
      }
    }
  };

  // Função para fazer upload da imagem
  const uploadImage = async (file) => {
    if (!file) return null;
    
    try {
      setUploadLoading(true);

      // Verificar o bucket e criar se não existir
      try {
        const { data: buckets, error: bucketsError } = await supabase
          .storage
          .listBuckets();
        
        if (bucketsError) {
          console.error('Erro ao verificar buckets:', bucketsError);
          throw bucketsError;
        }
        
        // Verificar se o bucket eventos existe
        const bucketExists = buckets.some(bucket => bucket.name === 'eventos');
        
        // Se não existir, criar o bucket
        if (!bucketExists) {
          console.log('Bucket "eventos" não encontrado. Criando...');
          const { error: createBucketError } = await supabase
            .storage
            .createBucket('eventos', {
              public: true, // Configurar como público
              fileSizeLimit: 5242880 // 5MB
            });
          
          if (createBucketError) {
            console.error('Erro ao criar bucket:', createBucketError);
            throw createBucketError;
          }
          console.log('Bucket "eventos" criado com sucesso');
        }
      } catch (bucketError) {
        console.error('Erro ao gerenciar buckets:', bucketError);
        alert('Erro ao verificar storage. Por favor, contate o administrador.');
        throw bucketError;
      }

      // Verificar o arquivo novamente
      if (file.size === 0) {
        alert('O arquivo selecionado está vazio.');
        return null;
      }

      // Verificar o tipo de arquivo
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!validImageTypes.includes(file.type)) {
        alert('Por favor, selecione um arquivo de imagem válido (JPG, PNG, GIF, WEBP, SVG).');
        return null;
      }

      // Gerar um nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      
      console.log(`Iniciando upload do arquivo: ${fileName} (${file.size} bytes)`);
      
      // Fazer o upload do arquivo com opção upsert
      const { data, error } = await supabase
        .storage
        .from('eventos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true // Tentar sobrescrever se já existir
        });
      
      if (error) {
        console.error('Detalhes do erro de upload:', error);
        alert(`Erro ao fazer upload: ${error.message || 'Erro desconhecido'}`);
        throw error;
      }
      
      console.log('Upload concluído com sucesso:', data);
      
      // Gerar URL pública para o arquivo
      const { data: publicURL } = supabase
        .storage
        .from('eventos')
        .getPublicUrl(fileName);
      
      console.log('URL gerada:', publicURL.publicUrl);
      return publicURL.publicUrl;
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      
      // Solução alternativa usando Base64 se o upload falhar
      if (window.confirm('Ocorreu um erro ao fazer upload da imagem para o servidor. Deseja usar um método alternativo?')) {
        try {
          const base64Image = await readFileAsBase64(file);
          return base64Image; // Retorna a imagem como base64
        } catch (base64Error) {
          console.error('Erro ao converter para base64:', base64Error);
          alert('Não foi possível processar a imagem. Por favor, tente com outra imagem.');
          return null;
        }
      }
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  // Função auxiliar para converter arquivo para base64
  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Função para configurar política de acesso público ao bucket (adicionar isso ao componente)
  const configureBucketPolicy = async (bucketName) => {
    try {
      // Definir política que permite acesso de leitura público
      const { error } = await supabase.rpc('create_storage_policy', {
        bucket_name: bucketName,
        policy_definition: {
          name: `${bucketName}_public_read`,
          definition: {
            storage: {
              bucket_id: bucketName,
              object_id: '*',
            },
            action: 'SELECT'
          },
          roles: ['anon']
        }
      });
      
      if (error) {
        console.error('Erro ao configurar política do bucket:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao configurar bucket:', error);
      return false;
    }
  };

  // Salvar o formulário (adicionar novo ou atualizar existente)
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    try {
      // Validar dados do formulário
      if (!formData.nome || !formData.data_inicio || !formData.data_fim) {
        alert('Por favor, preencha todos os campos obrigatórios.');
        return;
      }

      // Validar se data de fim é posterior à data de início
      if (new Date(formData.data_fim) < new Date(formData.data_inicio)) {
        alert('A data de fim deve ser posterior à data de início.');
        return;
      }
      
      // Upload da imagem, se houver
      let imageUrl = formData.imagem_url;
      if (formData.imagem) {
        try {
          imageUrl = await uploadImage(formData.imagem);
          if (!imageUrl) {
            throw new Error('Falha ao obter URL da imagem após upload.');
          }
        } catch (uploadError) {
          console.error('Erro no upload:', uploadError);
          alert('Erro ao fazer upload da imagem. Por favor, tente novamente.');
          return;
        }
      }
      
      // Preparar dados para o banco
      const eventData = {
        nome: formData.nome,
        descricao: formData.descricao,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim,
        local: formData.local,
        status: formData.status,
        ativo: formData.ativo,
        imagem_url: imageUrl,
        updated_at: new Date()
      };

      if (isEditing) {
        // Atualizar evento existente
        const { error } = await supabase
          .from('caex_eventos')
          .update(eventData)
          .eq('id', formData.id);
        
        if (error) throw error;
        
        console.log('Evento atualizado com sucesso');
        
        // Atualizar a lista de eventos localmente
        setEvents(events.map(event => 
          event.id === formData.id ? { ...event, ...eventData } : event
        ));
      } else {
        // Adicionar novo evento
        const { data, error } = await supabase
          .from('caex_eventos')
          .insert([eventData])
          .select();
        
        if (error) throw error;
        
        console.log('Evento adicionado com sucesso:', data);
        
        // Adicionar o novo evento à lista
        setEvents([data[0], ...events]);
      }
      
      // Fechar o modal e resetar o formulário
      setShowModal(false);
      setFormData({
        id: null,
        nome: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        local: '',
        status: 'pendente',
        ativo: true,
        imagem: null,
        imagem_url: ''
      });
      setImagePreview(null);
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      alert(`Não foi possível salvar o evento. Erro: ${error.message || 'Desconhecido'}`);
    }
  };

  // Formatar data para exibição
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Função para obter a cor do status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ativo':
        return '#28a745';
      case 'pendente':
        return '#ffc107';
      case 'cancelado':
        return '#dc3545';
      case 'concluido':
        return '#17a2b8';
      default:
        return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaCalendarAlt className="dashboard-title-icon" />
                Gestão de Eventos
              </h1>
            </div>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaCalendarAlt className="dashboard-title-icon" />
                Gestão de Eventos
              </h1>
            </div>
          </div>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
            <strong className="font-bold">Erro: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaCalendarAlt className="dashboard-title-icon" />
              Gestão de Eventos
            </h1>
            <p className="dashboard-subtitle">
              Gerenciamento de eventos do portal de expositores
            </p>
          </div>
        </div>

        {/* Botão para adicionar evento */}
        <div className="flex justify-end mb-6">
          {hasPermission('all') && (
            <button 
              onClick={handleAddNew}
              className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-all flex items-center gap-2"
              style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            >
              <FaPlus />
              Adicionar Evento
            </button>
          )}
        </div>

        {/* Tabela de eventos */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaCalendarAlt className="card-title-icon" />
              Lista de Eventos
            </h5>
          </div>
          <div className="modern-card-body">
            {events.length === 0 ? (
              <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-8 rounded-lg text-center my-4">
                <p className="text-xl">Nenhum evento encontrado.</p>
                <p className="mt-2">Clique em "Adicionar Evento" para cadastrar um novo.</p>
              </div>
            ) : (
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Imagem</th>
                      <th>Nome do Evento</th>
                      <th>Data Início</th>
                      <th>Data Fim</th>
                      <th>Local</th>
                      <th>Status</th>
                      <th>Ativo</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.id}>
                        <td>
                          {event.imagem_url ? (
                            <div style={{ 
                              width: '40px', 
                              height: '40px', 
                              borderRadius: '8px', 
                              overflow: 'hidden',
                              background: '#f8f9fa'
                            }}>
                              <img 
                                src={event.imagem_url} 
                                alt={event.nome} 
                                style={{ 
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>
                          ) : (
                            <div style={{ 
                              width: '40px', 
                              height: '40px', 
                              borderRadius: '8px', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              backgroundColor: '#f8f9fa',
                              color: '#adb5bd'
                            }}>
                              <FaImage />
                            </div>
                          )}
                        </td>
                        <td className="event-name">{event.nome}</td>
                        <td>{formatDisplayDate(event.data_inicio)}</td>
                        <td>{formatDisplayDate(event.data_fim)}</td>
                        <td>{event.local || '-'}</td>
                        <td>
                          <span className="modern-badge" style={{ 
                            backgroundColor: getStatusColor(event.status),
                            color: 'white',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {event.status}
                          </span>
                        </td>
                        <td>
                          <span className="modern-badge" style={{ 
                            backgroundColor: event.ativo ? '#28a745' : '#dc3545',
                            color: 'white',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {event.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="text-right">
                          {hasPermission('all') && (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(event)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.cancelButton
                                }}
                                className="btn-cancel"
                              >
                                <FaEdit className="mr-1" />
                                Editar
                              </button>
                              
                              <button
                                onClick={() => handleDelete(event.id)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.submitButton,
                                  background: '#dc3545'
                                }}
                                className="btn-submit"
                              >
                                <FaTrash className="mr-1" />
                                Excluir
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal para adicionar/editar */}
        {showModal && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  {isEditing ? <FaEdit style={{color: '#6c757d'}} /> : <FaPlus style={{color: '#6c757d'}} />}
                  {isEditing ? 'Editar Evento' : 'Adicionar Evento'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              <div style={modalStyles.body} className="modal-scrollable-body">
                <div className="form-group">
                  <label className="form-label" htmlFor="nome">
                    Nome do Evento <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Digite o nome do evento"
                    required
                  />
                </div>
                
                {/* Área de upload de imagem */}
                <div className="form-group">
                  <label className="form-label">
                    Imagem do Evento
                  </label>
                  <div 
                    style={modalStyles.imageUploadContainer} 
                    className="image-upload-container"
                    onClick={handleImageClick}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    
                    {uploadLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner inline-block h-8 w-8 border-2 border-t-2 border-gray-200 rounded-full" style={{ borderTopColor: '#6c757d' }}></div>
                        <p className="mt-2 text-gray-600">Fazendo upload...</p>
                      </div>
                    ) : imagePreview ? (
                      <div style={{ position: 'relative' }}>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={modalStyles.imagePreview} 
                        />
                        <button 
                          type="button" 
                          style={modalStyles.removeImageButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                        >
                          ✕
                        </button>
                        <div style={modalStyles.uploadButton} className="upload-button">
                          <FaUpload />
                          Alterar imagem
                        </div>
                      </div>
                    ) : (
                      <>
                        <FaImage style={modalStyles.uploadIcon} />
                        <p style={modalStyles.uploadText}>Clique para selecionar uma imagem</p>
                        <div style={modalStyles.uploadButton} className="upload-button">
                          <FaUpload />
                          Selecionar arquivo
                        </div>
                        <p className="text-muted mt-2">Formatos aceitos: JPG, PNG, GIF (max. 5MB)</p>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="descricao">
                    Descrição
                  </label>
                  <textarea
                    id="descricao"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Digite uma descrição para o evento"
                    rows="3"
                  />
                  <small className="text-muted">Descrição opcional do evento</small>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="data_inicio">
                        Data de Início <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="date"
                        id="data_inicio"
                        name="data_inicio"
                        value={formData.data_inicio}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="data_fim">
                        Data de Fim <span style={{ color: '#dc3545' }}>*</span>
                      </label>
                      <input
                        type="date"
                        id="data_fim"
                        name="data_fim"
                        value={formData.data_fim}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="local">
                    Local do Evento
                  </label>
                  <input
                    type="text"
                    id="local"
                    name="local"
                    value={formData.local}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Digite o local onde será realizado o evento"
                  />
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="status">
                        Status
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="pendente">Pendente</option>
                        <option value="ativo">Ativo</option>
                        <option value="concluido">Concluído</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">
                        <input
                          type="checkbox"
                          name="ativo"
                          checked={formData.ativo}
                          onChange={handleChange}
                          style={{ marginRight: '0.5rem' }}
                        />
                        Evento Ativo
                      </label>
                      <small className="text-muted">Marque para manter o evento visível no sistema</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  style={{...modalStyles.footerButton, ...modalStyles.submitButton}}
                  className="btn-submit"
                  disabled={uploadLoading}
                >
                  {uploadLoading ? (
                    <>
                      <span className="spinner inline-block h-4 w-4 border-2 border-t-2 border-white rounded-full mr-2" style={{ borderTopColor: 'transparent' }}></span>
                      Processando...
                    </>
                  ) : (
                    isEditing ? 'Atualizar' : 'Adicionar'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManagement;